import type { ReactNode } from 'react';
import { ArrowDown, ArrowRight, ArrowUp, Info } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn, formatPercent } from '@/lib/utils';
import { Sparkline } from './sparkline';

export interface StatCardProps {
  label: string;
  value: ReactNode;
  /** Signed share of change, e.g. `0.124` for +12,4 %. */
  delta?: number;
  deltaLabel?: string;
  sparkline?: readonly number[];
  hint?: string;
  loading?: boolean;
  /** For metrics where going down is the good news (churn, tiempo de respuesta). */
  invertDelta?: boolean;
  className?: string;
}

const DIRECTION = {
  up: { Icon: ArrowUp, sign: '+' },
  down: { Icon: ArrowDown, sign: '−' },
  flat: { Icon: ArrowRight, sign: '' },
} as const;

function direction(delta: number): 'up' | 'down' | 'flat' {
  if (Math.abs(delta) < 0.0005) return 'flat';
  return delta > 0 ? 'up' : 'down';
}

/** A KPI tile: big tabular number, signed delta, and the trend behind it. */
export function StatCard({
  label,
  value,
  delta,
  deltaLabel,
  sparkline,
  hint,
  loading = false,
  invertDelta = false,
  className,
}: StatCardProps) {
  const trend = delta === undefined ? 'flat' : direction(delta);
  const { Icon, sign } = DIRECTION[trend];

  // Direction and sentiment are separate: churn going down is a win.
  const rising = trend === 'up';
  const sentiment: 'up' | 'down' | 'flat' =
    trend === 'flat' ? 'flat' : rising !== invertDelta ? 'up' : 'down';

  return (
    <div
      className={cn(
        'flex flex-col justify-between gap-3 rounded-lg border border-border bg-background p-4',
        className,
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <span className="text-xs text-muted-foreground">{label}</span>
        {hint && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  aria-label={`Qué mide ${label}`}
                  className="rounded-sm text-muted-foreground transition-colors duration-150 ease-out hover:text-foreground"
                >
                  <Info className="size-3.5" />
                </button>
              </TooltipTrigger>
              <TooltipContent className="max-w-56">{hint}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>

      {loading ? (
        <div className="space-y-2">
          <Skeleton className="h-8 w-24" />
          <Skeleton className="h-3 w-32" />
        </div>
      ) : (
        <>
          <div className="flex items-baseline gap-2">
            <span className="text-3xl font-medium tabular leading-none">{value}</span>
          </div>

          {(delta !== undefined || deltaLabel) && (
            <div className="flex items-center gap-1.5 text-xs">
              {delta !== undefined && (
                <span
                  className={cn(
                    'inline-flex items-center gap-0.5 font-medium tabular',
                    sentiment === 'up' && 'text-success',
                    sentiment === 'down' && 'text-destructive',
                    sentiment === 'flat' && 'text-muted-foreground',
                  )}
                >
                  <Icon className="size-3" aria-hidden />
                  {sign}
                  {formatPercent(Math.abs(delta), 1)}
                </span>
              )}
              {deltaLabel && <span className="text-muted-foreground">{deltaLabel}</span>}
            </div>
          )}
        </>
      )}

      {sparkline && sparkline.length > 1 && !loading && (
        <Sparkline values={sparkline} trend={sentiment} />
      )}
    </div>
  );
}
